import { Link } from 'react-router-dom';
import type { Article } from '@/types';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors no-underline"
      >
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.date} · {article.readTime}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors no-underline"
      >
        <div className="w-full sm:w-48 h-48 sm:h-32 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 mt-2 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 mt-3">
            <img
              src={article.authorAvatar}
              alt={article.author}
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-xs text-foreground-600">{article.author}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {article.featured && (
          <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
            精选
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 mt-2 line-clamp-2 flex-1">{article.excerpt}</p>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
          <img
            src={article.authorAvatar}
            alt={article.author}
            className="w-6 h-6 rounded-full object-cover"
          />
          <span className="text-xs text-foreground-600">{article.author}</span>
          <span className="text-xs text-foreground-400 ml-auto">{article.date}</span>
        </div>
      </div>
    </Link>
  );
}